<section [ngClass]="containerClass">
  <div
    *ngIf="config.showTabs && config.tabs?.length"
    class="mb-3 flex flex-col gap-3 border-b border-slate200 pb-3 lg:flex-row lg:items-center lg:justify-between"
  >
    <div class="flex flex-wrap gap-2">
      <button
        *ngFor="let tab of config.tabs"
        type="button"
        (click)="onTabChange(tab.value)"
        class="inline-flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition"
        [ngClass]="
          activeTab === tab.value
            ? 'border-indigo600 bg-indigo600 text-white'
            : 'border-slate200 bg-white text-slate600 hover:border-slate300 hover:bg-slate50 hover:text-slate900'
        "
      >
        <span>{{ tab.label }}</span>
        <span
          *ngIf="tab.count !== undefined"
          class="rounded-full px-2 py-0.5 text-xs"
          [ngClass]="
            activeTab === tab.value ? 'bg-white/20 text-white' : 'bg-slate100 text-slate600'
          "
        >
          {{ tab.count }}
        </span>
      </button>
    </div>

    <div *ngIf="config.showMarkAll" class="flex justify-start lg:justify-end">
      <button
        type="button"
        (click)="markAllRead.emit()"
        class="inline-flex cursor-pointer items-center gap-2 rounded-md border border-emerald600 bg-emerald600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald700"
      >
        <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"></path>
        </svg>
        <span>Mark All as Read</span>
      </button>
    </div>
  </div>

  <form [formGroup]="filterForm" class="flex flex-wrap items-stretch gap-2.5 sm:items-center lg:gap-3">
    <div *ngIf="config.showSearch" class="relative w-full sm:w-[220px] lg:w-[240px]">
      <span class="pointer-events-none absolute inset-y-0 left-3 inline-flex items-center">
        <svg
          class="h-4 w-4 text-slate600"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z"
          ></path>
        </svg>
      </span>
      <input
        type="text"
        formControlName="search"
        [placeholder]="config.searchPlaceholder || 'Search'"
        style="font-size: 12px; font-weight: 400"
        class="f-12 h-9 w-full rounded-md border border-slate300 bg-white pl-9 pr-4 text-slate600 outline-none transition placeholder:text-slate400"
      />
    </div>

    <div *ngFor="let filter of config.filters" class="w-full sm:w-[calc(50%-0.3125rem)] lg:w-fit lg:min-w-[100px]">
      <app-select
        *ngIf="filter.type === 'select'"
        class="w-full"
        [form]="filterForm"
        [controlName]="filter.key"
        [options]="getSelectOptions(filter)"
        [placeholder]="filter.label"
        [searchable]="!!filter.searchable"
        [fitDropdownToContent]="true"
        size="sm"
      ></app-select>

      <input
        *ngIf="filter.type !== 'select'"
        [type]="filter.type"
        [formControlName]="filter.key"
        [placeholder]="filter.placeholder || filter.label"
        class="h-9 w-full rounded-md border border-slate300 bg-white px-4 text-xs font-semibold leading-[14px] text-slate600 outline-none transition placeholder:font-medium placeholder:text-slate400 focus:border-slate500"
        [ngClass]="filter.type === 'date' ? '!text-xs !text-slate600 !font-semibold' : ''"
      />
    </div>

    <div *ngIf="config.showDateRange" class="relative w-full sm:w-[260px] lg:w-auto" data-date-range-picker>
      <button
        type="button"
        (click)="toggleDatePicker()"
        class="inline-flex h-9 w-full max-w-full cursor-pointer items-center justify-between gap-2 rounded-md border border-slate300 bg-white px-4 outline-none focus:outline-none lg:w-fit"
      >
        <app-icon-reusable
          [icon]="calenderIcon"
          [size]="16"
          classes="shrink-0 text-slate400 mt-1"
        ></app-icon-reusable>
        <span class="truncate whitespace-nowrap text-xs font-medium leading-4 text-slate500">{{
          getDateRangeDisplay()
        }}</span>
      </button>

      <div
        *ngIf="isDatePickerOpen"
        class="absolute left-0 top-[calc(100%+8px)] z-40 w-max min-w-full max-w-[calc(100vw-2rem)] overflow-hidden rounded-[8px] border border-slate300 bg-white shadow-[0_6px_16px_rgba(15,23,42,0.18)]"
      >
        <div
          class="grid grid-cols-1 divide-y divide-slate200 lg:grid-cols-2 lg:divide-x lg:divide-y-0"
        >
          <div class="p-3">
            <div class="mb-3 flex items-center justify-between px-1">
              <button
                type="button"
                (click)="goToPreviousMonth('left')"
                class="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-slate500 hover:bg-slate50"
              >
                <svg
                  class="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 15 7 10l5-5"></path>
                </svg>
              </button>
              <div class="flex items-center gap-2">
                <select
                  class="h-8 cursor-pointer rounded-md border border-slate300 bg-white px-2 text-xs text-slate700 outline-none"
                  [value]="leftCalendarMonth.getMonth().toString()"
                  (change)="onCalendarMonthChange('left', $any($event.target).value)"
                >
                  <option
                    *ngFor="let month of monthOptions"
                    [value]="month.value"
                    [selected]="month.value === leftCalendarMonth.getMonth()"
                  >
                    {{ month.label }}
                  </option>
                </select>
                <select
                  class="h-8 cursor-pointer rounded-md border border-slate300 bg-white px-2 text-xs text-slate700 outline-none"
                  [value]="leftCalendarMonth.getFullYear().toString()"
                  (change)="onCalendarYearChange('left', $any($event.target).value)"
                >
                  <option
                    *ngFor="let year of yearOptions"
                    [value]="year"
                    [selected]="year === leftCalendarMonth.getFullYear()"
                  >
                    {{ year }}
                  </option>
                </select>
              </div>
              <button
                type="button"
                (click)="goToNextMonth('left')"
                class="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-slate500 hover:bg-slate50"
              >
                <svg
                  class="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="m8 5 5 5-5 5"></path>
                </svg>
              </button>
            </div>

            <div class="mb-2 grid grid-cols-7 gap-y-2">
              <span
                *ngFor="let day of weekDays"
                class="text-center text-sm font-semibold leading-4 text-slate700"
                >{{ day }}</span
              >
            </div>

            <div class="grid grid-cols-7 gap-y-1 RH6">
              <button
                *ngFor="let day of getCalendarDays(leftCalendarMonth)"
                type="button"
                (click)="onDateSelect(day.iso)"
                class="RH6 mx-auto inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full"
                [ngClass]="{
                  'text-slate400 opacity-70': !day.inCurrentMonth && !isSelectedDate(day.iso),
                  'text-slate700':
                    day.inCurrentMonth && !isSelectedDate(day.iso) && !isInSelectedRange(day.iso),
                  'bg-indigo600 text-white hover:bg-': isSelectedDate(day.iso),
                  'bg-slate100 text-slate900':
                    isInSelectedRange(day.iso) && !isSelectedDate(day.iso),
                }"
              >
                {{ day.day }}
              </button>
            </div>
          </div>

          <div class="p-3">
            <div class="mb-3 flex items-center justify-between px-1">
              <button
                type="button"
                (click)="goToPreviousMonth('right')"
                class="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-slate500 hover:bg-slate50"
              >
                <svg
                  class="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 15 7 10l5-5"></path>
                </svg>
              </button>
              <div class="flex items-center gap-2">
                <select
                  class="h-8 cursor-pointer rounded-md border border-slate300 bg-white px-2 text-xs text-slate700 outline-none"
                  [value]="rightCalendarMonth.getMonth().toString()"
                  (change)="onCalendarMonthChange('right', $any($event.target).value)"
                >
                  <option
                    *ngFor="let month of monthOptions"
                    [value]="month.value"
                    [selected]="month.value === rightCalendarMonth.getMonth()"
                  >
                    {{ month.label }}
                  </option>
                </select>
                <select
                  class="h-8 cursor-pointer rounded-md border border-slate300 bg-white px-2 text-xs text-slate700 outline-none"
                  [value]="rightCalendarMonth.getFullYear().toString()"
                  (change)="onCalendarYearChange('right', $any($event.target).value)"
                >
                  <option
                    *ngFor="let year of yearOptions"
                    [value]="year"
                    [selected]="year === rightCalendarMonth.getFullYear()"
                  >
                    {{ year }}
                  </option>
                </select>
              </div>
              <button
                type="button"
                (click)="goToNextMonth('right')"
                class="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-slate500 hover:bg-slate50"
              >
                <svg
                  class="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="m8 5 5 5-5 5"></path>
                </svg>
              </button>
            </div>

            <div class="mb-2 grid grid-cols-7 gap-y-2">
              <span
                *ngFor="let day of weekDays"
                class="text-center text-sm font-semibold leading-4 text-slate700"
                >{{ day }}</span
              >
            </div>

            <div class="grid grid-cols-7 gap-y-1 RH6">
              <button
                *ngFor="let day of getCalendarDays(rightCalendarMonth)"
                type="button"
                (click)="onDateSelect(day.iso)"
                class="RH6 mx-auto inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full"
                [ngClass]="{
                  'text-slate400 opacity-70': !day.inCurrentMonth && !isSelectedDate(day.iso),
                  'text-slate700':
                    day.inCurrentMonth && !isSelectedDate(day.iso) && !isInSelectedRange(day.iso),
                  'bg-slate200 text-slate900': isSelectedDate(day.iso),
                  'bg-slate100 text-slate900':
                    isInSelectedRange(day.iso) && !isSelectedDate(day.iso),
                }"
              >
                {{ day.day }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="order-3 flex w-full flex-wrap items-center gap-2 sm:order-none sm:w-auto">
      <app-button-reusable
        *ngIf="config.showReset"
        class="[&>button]:h-9 [&>button]:w-full [&>button]:px-4 [&>button]:py-2 sm:[&>button]:w-auto !bg-white rounded-md"
        variant="danger"
        appearance="outline"
        size="sm"
        [disabled]="resetDisabled"
        (clicked)="onReset()"
      >
        <span class="BBody inline-flex items-center font-semibold leading-none ">Reset all</span>
        <app-icon-reusable
          [icon]="resetIcon"
          [size]="16"
          class="ml-2 mt-0.5"
          [useCurrentColor]="true"
          [classes]="resetIconClasses"
        ></app-icon-reusable>
      </app-button-reusable>
    </div>

    <div
      *ngIf="config.showReassign"
      class="order-4 flex w-full flex-wrap items-center gap-2 sm:order-none sm:ml-auto sm:w-auto"
      [appTooltip]="reassignTooltip"
    >
      <app-button-reusable
        class="[&>button]:h-9 [&>button]:w-full [&>button]:px-4 [&>button]:py-2 sm:[&>button]:w-auto"
        variant="primary"
        appearance="outline"
        size="md"
        [disabled]="reassignDisabled"
        (clicked)="onReassign()"
      >
        <!-- <app-icon-reusable
          [icon]="reassignIcon"
          [size]="16"
          [useCurrentColor]="true"
          [classes]="'text-inherit shrink-0'"
        ></app-icon-reusable> -->
        <span class="BBody inline-flex items-center font-semibold leading-none">Reassign Client</span>
      </app-button-reusable>
    </div>

    <div
      *ngIf="config.showExport"
      class="order-5 flex w-full flex-wrap items-center gap-2 sm:order-none sm:w-auto"
      [ngClass]="config.showReassign ? '' : 'sm:ml-auto'"
      [appTooltip]="exportPermission ? '' : 'Request administrator for access'"
    >
      <app-button-reusable
        class="[&>button]:h-9 [&>button]:w-full [&>button]:px-4 [&>button]:py-2 sm:[&>button]:w-auto"
        [disabled]="exportDisabled || !exportPermission"
        variant="neutral"
        appearance="outline"
        size="sm"
        (clicked)="onExport()"
      >
        <app-icon-reusable
          [icon]="exportIcon"
          [size]="16"
         
          [useCurrentColor]="true"
          [classes]="exportIconClasses"
        ></app-icon-reusable>
        <span class="BBody inline-flex items-center font-semibold leading-none ml-2 ">Export</span>
      </app-button-reusable>
    </div>
  </form>

  <div
    *ngIf="config.showFilterChips && activeFilterChips.length > 0"
    class="mt-3 flex flex-col gap-2.5 border-t border-slate200 pt-3 lg:flex-row lg:items-center"
  >
    <span class="text-sm font-medium text-slate500">Active filters</span>

    <div class="flex flex-1 flex-wrap gap-2">
      <button
        *ngFor="let chip of activeFilterChips"
        type="button"
        (click)="removeFilter(chip.key)"
        class="inline-flex cursor-pointer items-center gap-2 rounded-full bg-sky100 px-3 py-1.5 text-xs font-medium text-sky800 transition hover:bg-sky200"
      >
        <span>{{ chip.label }}</span>
        <span class="text-sm leading-none">×</span>
      </button>
    </div>

    <button
      *ngIf="config.showReset"
      type="button"
      (click)="onReset()"
      class="inline-flex cursor-pointer items-center text-sm font-medium text-slate500 transition hover:text-slate800"
    >
      Clear all
    </button>
  </div>
</section>
